import find from 'lodash/find';
import filter from 'lodash/filter';
import omit from 'lodash/omit';
import compact from 'lodash/compact';
import get from 'lodash/get';
import head from 'lodash/head';
import includes from 'lodash/includes';
import isNil from 'lodash/isNil';
import map from 'lodash/map';
import reject from 'lodash/reject';
import mapValues from 'lodash/mapValues';

import { getIdFrom, getUuidFrom } from 'zoomforth_codesbane_viewer/lib/id_utils.js';

import { combineReducers } from 'redux';

import { getSerializableContentDataForCopy } from 'zoomforth_codesbane_viewer/lib/page_content_utils.js';

import {
  isOrderedContentSection,
  isSingletonContentSection,
  isGridPositionsContentSection
} from 'zoomforth_codesbane_viewer/lib/sections.js';

import viewerUI, * as fromViewerUI from './viewer_ui.js';
import filteredApiFeeds, * as fromFilteredApiFeeds from './filtered_api_feeds.js';
import consent, * as fromConsent from './consent.js';
import translations, * as fromTranslations from './translations.js';
import pageEntities, * as fromPageEntities from './page_entities.selectors.js';

import theme, * as fromTheme from './theme.selectors.js';
import { SECTION_TYPE_TABS } from 'zoomforth_codesbane_viewer/const/sections.js';

const dummyReducer = (state = {})=> (state); // Noop reducer--we never change page entity data in viewer


export default combineReducers({
  // assigns each slice reducers returned state to the eponymous key on the final state object
  // understand this behavior!: http://redux.js.org/docs/api/combineReducers.html
  viewerUI,
  filteredApiFeeds,
  consent,
  pageEntities,
  theme,
  translations,
  pageMeta: dummyReducer,
});


export const getIsLoadingFilteredApiFeed = (state)=>
  fromFilteredApiFeeds.getIsLoading(state.filteredApiFeeds);

export const getErrorInFilteredApiFeed = (state)=>
  fromFilteredApiFeeds.getErrorInFilteredApiFeed(state.filteredApiFeeds);

export const getFilteredApiFeed = (state, apiFeedId, { filters })=>
  fromFilteredApiFeeds.getFilteredApiFeed(state.filteredApiFeeds, apiFeedId, { filters });

export const getActiveOverlayContent = (state)=> {
  const activeUuid = fromViewerUI.getActiveOverlayContentUuid(state.viewerUI);
  if (activeUuid) {
    return fromPageEntities.getContentItemByUuid(state.pageEntities, activeUuid);
  }
  return null;
};

export const getIsActiveOverlayOpenedFromSlidehsow = (state)=>
  fromViewerUI.getIsActiveOverlayOpenedFromSlidehsow(state.viewerUI);

export const getStylePresets = (state)=>
  fromTheme.getStylePresets(state.theme);

export const getCurrentThemeUuid = (state)=>
  fromTheme.getCurrentThemeUuid(state.theme);

export const getMostCommonThemeFontColors = (state)=>
  fromTheme.getMostCommonThemeFontColors(state.theme);

export const getMostCommonThemeBackgroundColors = (state)=>
  fromTheme.getMostCommonThemeBackgroundColors(state.theme);

export const getAssetByIdent = (state, assetIdent)=>
  fromPageEntities.getAssetByIdent(state.pageEntities, assetIdent);

export const getCssClassesListForContent = (state, contentItemOrUuid)=> {
  const contentUuid = getUuidFrom(contentItemOrUuid);
  const contentItem = getContentItemByUuid(state, contentUuid);

  return fromPageEntities.getCssClassesListForContent(state.pageEntities, contentItem);
};

export const getCssClassesListForOverlay = (state, contentItemOrUuid)=> {
  const contentUuid = getUuidFrom(contentItemOrUuid);
  const contentItem = getContentItemByUuid(state, contentUuid);

  return fromPageEntities.getCssClassesListForOverlay(state.pageEntities, contentItem);
};

export const getAssetForContent = (state, contentItemOrUuid)=> {
  if (!contentItemOrUuid) {
    return null;
  }

  const contentUuid = getUuidFrom(contentItemOrUuid);
  const contentItem = getContentItemByUuid(state, contentUuid);

  if (!contentItem || !contentItem.asset_id || !contentItem.asset_type) {
    return null;
  }

  return fromPageEntities.getAssetForContent(state.pageEntities, contentItem);
};

export const getCurrentSubpage = (state)=> {
  const spid = fromViewerUI.getCurrentSubpageId(state.viewerUI);
  // default to home subpage
  if (!spid) {
    return getHomeSubpage(state);
  }
  return getSubpageById(state, spid);
};

export const getBlocksData = (state) =>
  fromPageEntities.getBlocksData(state.pageEntities);

export const getContentItemByUuid = (state, uuid)=>
  fromPageEntities.getContentItemByUuid(state.pageEntities, uuid);

export const getContentUuidsForAssetId = (state, assetId)=>
  fromPageEntities.getContentUuidsForAssetId(state.pageEntities, assetId);

export const getCustomFooterHtml = (state)=> 
  fromTheme.getCustomFooterHtml(state.theme);

export const getExistentTargetsForAppAction = (state, { sectionId, contentUuid })=>
  fromPageEntities.getExistentTargetsForAppAction(state.pageEntities, { sectionId, contentUuid });

export const getPageEntityFuzzySearcher = (state)=>
  fromPageEntities.getPageEntityFuzzySearcher(state.pageEntities);

export const getHomeSubpage = (state)=> {
  const homeSubpageMenuItem = getHomeSubpageMenuItem(state);
  return getSubpageById(state, homeSubpageMenuItem.appAction.subpageId);
};

export const getHomeSubpageMenuItem = (state)=> {
  return find(
    getOrderedMainMenuItems(state), 
    ({ isPrimaryForSubpage, appAction }) => 
      isPrimaryForSubpage && 
      !getSubpageById(state, appAction.subpageId).isHidden
  );
};

export const getInPageSearch = (state)=>
  fromViewerUI.getInPageSearch(state.viewerUI);

export const getIsCurrentSubpageFullPageSection = (state)=> {
  const currentSubpage = getCurrentSubpage(state);
  const sections = map(currentSubpage.orderedSectionIds, sectionId => 
    getSectionById(state, sectionId)
  );
  return (
    sections.length === 1 &&
    includes(['slideshow', 'videoplaylist', 'pdf', 'text'], sections[0].type)
  );
};

export const getIsInPageSearchEnabled = (state)=>
  fromPageEntities.getIsInPageSearchEnabled(state.pageEntities);

export const getIsInPageSearchDisplayed = (state)=>
  fromViewerUI.getIsInPageSearchDisplayed(state.viewerUI);

export const getIsNeedsRefreshDisplayed = (state)=>
  fromViewerUI.getIsNeedsRefreshDisplayed(state.viewerUI);

export const getViewerType = (state)=>
  fromViewerUI.getViewerType(state.viewerUI);

export const getIsMobileRenderEnabled = (state)=>
  fromPageEntities.getIsMobileRenderEnabled(state.pageEntities);

export const getIsSecondaryLogoEnabled = (state)=>
  fromPageEntities.getIsSecondaryLogoEnabled(state.pageEntities);
  
export const getIsScrollingToTop = (state)=>
  fromViewerUI.getIsScrollingToTop(state.viewerUI);
  
export const getIsUsingV1Styles = (state)=>
  fromTheme.getIsUsingV1Styles(state.theme);

export const getLastClickedSubmenuItemId = (state)=> 
  fromViewerUI.getLastClickedSubmenuItemId(state.viewerUI);

export const getLastClickedToplevelInternalLinkId = (state)=> 
  fromViewerUI.getLastClickedToplevelInternalLinkId(state.viewerUI);

export const getPreviewCollaborationSettings = (state)=> 
  fromViewerUI.getPreviewCollaborationSettings(state.viewerUI);

export const getMenuItemBySubpageId = (state, subpageId) =>
  fromPageEntities.getMenuItemBySubpageId(state.pageEntities, subpageId);

export const getMenuItemById = (state, id)=>
  fromPageEntities.getMenuItemById(state.pageEntities, id);

export const getMenuItemIdToMenuItemMap = (state)=>
  fromPageEntities.getMenuItemIdToMenuItemMap(state.pageEntities);

export const getMenuItemsForSection = (state, sectionOrId)=> {
  const sectionId = getIdFrom(sectionOrId);
  const allMenuItems = getMenuItemIdToMenuItemMap(state);
  return filter(allMenuItems, (menuItem)=> {
    return get(menuItem, 'appAction.sectionId') === sectionId;
  });
};

export const getOrderedContentUuidsForSection = (state, sectionOrId)=>
  fromPageEntities.getOrderedContentUuidsForSection(state.pageEntities, sectionOrId);

export const getOrderedNonEmptyContentItemsForSection = (state, sectionOrId)=>
  compact(map(getOrderedContentUuidsForSection(state, sectionOrId), uuid => {
    const item = getContentItemByUuid(state, uuid);
    const asset = getAssetForContent(state, item);
    if (!item || !asset) {
      // TODO: Determine why there are ever missing content or assets
      return;
    }
    return item;
  }));

export const getOrderedAndSelectedNonEmptyContentItemsForSection = (state, sectionOrId)=> { 
  // HACK: It would be nice if `getSectionSelectedContent` was smart enough to not return
  // null if there actually were content which should be implicitly selected
  const contentItems = getOrderedNonEmptyContentItemsForSection(state, sectionOrId);
  let selectedContentItem = getSectionSelectedContent(state, sectionOrId);
  if (isNil(selectedContentItem)) {
    selectedContentItem = head(contentItems);
  }
  return [contentItems, selectedContentItem];
};

export const getOrderedMainMenuItems = (state)=>
  fromPageEntities.getOrderedMainMenuItems(state.pageEntities);

export const getOrderedSubmenuItemsForSubpage = (state, subpageOrId)=>
  fromPageEntities.getOrderedSubmenuItemsForSubpage(state.pageEntities, subpageOrId);

export const getOrderedVisibleMainMenuItems = (state)=>
  reject(getOrderedMainMenuItems(state), (menuItem) => (
    (
      menuItem.appAction.subpageId &&
      get(getSubpageById(state, menuItem.appAction.subpageId), 'isHidden')
    ) ||
    (
      menuItem.appAction.sectionId &&
      get(getSectionById(state, menuItem.appAction.sectionId), 'isHidden')
    ) ||
    menuItem.isHidden
  ));

export const getVisibleSubmenuItemsBySubpageId = (state)=>
  mapValues(getSubpageIdToOrderedSubmenuItemIdsMap(state), (submenuItems) => (
    reject(submenuItems, (item) => (
      item.title == '' ||
      (
        item.appAction.sectionId &&
        get(getSectionById(state, item.appAction.sectionId), 'isHidden')
      )
    ))
  ));

export const getPageThemeUuid = (state)=>
  fromPageEntities.getPageThemeUuid(state.pageEntities);

export const getSiteViewerVersion = (state)=>
  fromPageEntities.getSiteViewerVersion(state.pageEntities);

export const getTranslationsIsEnabled = (state)=>
  fromPageEntities.getTranslationsIsEnabled(state.pageEntities);

export const getTranslationsDefaultLanguageIso = (state)=>
  fromPageEntities.getTranslationsDefaultLanguageIso(state.pageEntities);

export const getTranslationsAppliedLanguages = (state)=>
  fromPageEntities.getTranslationsAppliedLanguages(state.pageEntities);

export const getTranslationsCurrentLanguageIso = (state)=>
  fromPageEntities.getTranslationsCurrentLanguageIso(state.pageEntities);

export const getTranslationsSelectedLanguageIsos = (state)=>
  fromPageEntities.getTranslationsSelectedLanguageIsos(state.pageEntities);

export const getPageScopedCss = (state)=>
  fromPageEntities.getPageScopedCss(state.pageEntities);

export const getPageTitle = (state)=>
  fromPageEntities.getPageTitle(state.pageEntities);

export const getPrimaryMenuItemForSubpage = (state, subpageOrId)=>
  find(
    fromPageEntities.getOrderedMainMenuItems(state.pageEntities),
    menuItem => (
      menuItem.isPrimaryForSubpage &&
      get(menuItem,'appAction.subpageId') === getIdFrom(subpageOrId)
    )
  );

export const getRenderAsMobile = (state)=>
  fromViewerUI.getRenderAsMobile(state.viewerUI);

export const getScrollingToSectionId = (state)=>
  fromViewerUI.getScrollingToSectionId(state.viewerUI);

export const getScrollingToTileUuid = (state)=>
  fromViewerUI.getScrollingToTileUuid(state.viewerUI);

export const getScrollingToAccordionItemUuid = (state)=>
  fromViewerUI.getScrollingToAccordionItemUuid(state.viewerUI);

export const getScrollingAnimationTimeMs = (state)=>
  fromViewerUI.getScrollingAnimationTimeMs(state.viewerUI);

export const getSecondaryLogoData = (state) => {
  const isSecondaryLogoEnabled = getIsSecondaryLogoEnabled(state);
  const secondaryLogoData = fromPageEntities.getSecondaryLogoData(state.pageEntities);

  if (!isSecondaryLogoEnabled || !secondaryLogoData) {
    return {};
  }

  const { contentUuid } = secondaryLogoData;
  const contentItem = contentUuid ? getContentItemByUuid(state, contentUuid) : null;
  const asset = getAssetForContent(state, contentItem);

  return {
    ...secondaryLogoData,
    contentItem,
    asset
  };
};

export const getSections = (state)=>
  fromPageEntities.getSections(state.pageEntities);

export const getSectionAllowAutoplayContentUuid = (state, sectionOrId)=> 
  fromViewerUI.getSectionAllowAutoplayContentUuid(state.viewerUI, sectionOrId);

/**
 * Return the contentItem and asset associated with a section's backgroundData
 * attribute.
 *
 * @param {object} backgroundData - should be an object which has references to
 *                                  contentUuids for either images or videos.
 *
 * @return object
 */
export const getSectionBackgroundContentAndAssetFromBackgroundData = (state, backgroundData) => {
  const {
    backgroundType,
    backgroundImageContentUuid,
    backgroundVideoContentUuid
  } = backgroundData;

  const contentUuid = (
    backgroundType === 'image' ?
      backgroundImageContentUuid
    : backgroundType === 'video' ?
      backgroundVideoContentUuid
    :
      null
  );

  const contentItem = contentUuid ? getContentItemByUuid(state, contentUuid) : null;
  const asset = getAssetForContent(state, contentItem);

  return {
    asset,
    contentItem
  };
};


export const getSectionBackgroundData = (state, section, dataKey = 'backgroundData') => {
  if (!section) {
    return { backgroundType: 'none' };
  }

  // handle backwards compat since backgroundColor was added first to sections.
  if (dataKey === 'backgroundData' && section.backgroundColor) {
    return {
      backgroundType: 'color',
      backgroundColor: section.backgroundColor
    };
  }

  if (!section[dataKey]) {
    return { backgroundType: 'none' };
  }

  return {
    ...section[dataKey],
    ...getSectionBackgroundContentAndAssetFromBackgroundData(
      state,
      section[dataKey]
    )
  };
};


export const getSectionFallbackBackgroundData = (state, section) => {
  if (!section) {
    return { backgroundType: 'none' };
  }

  // backgroundVideoColor is now deprecated but can exist for slightly older data
  if (section.backgroundVideoColor) {
    return {
      backgroundType: 'color',
      backgroundColor: section.backgroundVideoColor
    };
  }

  if (!section.fallbackBackgroundData) {
    return { backgroundType: 'none' };
  }

  return {
    ...section.fallbackBackgroundData,
    ...getSectionBackgroundContentAndAssetFromBackgroundData(
      state,
      section.fallbackBackgroundData
    )
  };
};

/**
 * Return the border data for a section.
 *
 * @param {object} state - Redux state
 * @param {object} section - Section object
 * @param {string} dataKey - Key for border data (defaults to 'borderData')
 *
 * @return object
 */
export const getSectionBorderData = (state, section, dataKey = 'borderData') => {
  if (!section) {
    return { borderType: 'none' };
  }

  if (!section[dataKey]) {
    return { borderType: 'none' };
  }

  return {
    borderType: 'none',
    borderWidth: 1,
    borderColor: '#000000',
    isBorderRadiusEnabled: false,
    borderRadiusTop: 0,
    borderRadiusRight: 0,
    borderRadiusBottom: 0,
    borderRadiusLeft: 0,
    ...section[dataKey]
  };
};


export const getSectionById = (state, id)=>
  fromPageEntities.getSectionById(state.pageEntities, id);

export const getSectionForContent = (state, contentOrUuid)=>
  fromPageEntities.getSectionForContent(state.pageEntities, contentOrUuid);

export const getSectionGridPositions = (state, sectionOrId)=>
  fromPageEntities.getSectionGridPositions(state.pageEntities, sectionOrId);
  
export const getSectionOrderedContentItems = (state, sectionOrId)=>
  fromPageEntities.getSectionOrderedContentItems(state.pageEntities, sectionOrId);

export const getSectionsForSubpage = (state, subpageOrId)=>
  fromPageEntities.getSectionsForSubpage(state.pageEntities, subpageOrId);

export const getSectionSelectedContentUuid = (state, sectionOrId)=> {
  const section = getSectionById(state, getIdFrom(sectionOrId));
  return fromViewerUI.getSectionSelectedContentUuid(state.viewerUI, section);
};

export const getAppliedTagIdsByCategoryIdForSection = (state, sectionOrId)=>
  fromViewerUI.getAppliedTagIdsByCategoryIdForSection(state.viewerUI, sectionOrId);

export const getShouldRenderHeader = (state)=>
  fromViewerUI.getShouldRenderHeader(state.viewerUI);

export const getShouldRenderNavigation = (state)=>
  fromViewerUI.getShouldRenderNavigation(state.viewerUI);

export const getShouldRenderFooter = (state)=>
  fromViewerUI.getShouldRenderFooter(state.viewerUI);

export const getOpenIndicesForAccordion = (state, sectionId)=>
  fromViewerUI.getOpenIndicesForAccordion(state.viewerUI, sectionId);

export const getActiveTabId = (state, sectionId)=>
  fromViewerUI.getActiveTabId(state.viewerUI, sectionId);

export const getSectionSelectedContent = (state, sectionOrId)=> {
  const section = getSectionById(state, getIdFrom(sectionOrId));
  const selectedUuid = getSectionSelectedContentUuid(state, section);

  let selectedContent = null;
  if (selectedUuid) {
    selectedContent = fromPageEntities.getContentItemByUuid(state.pageEntities, selectedUuid);
  }
  return selectedContent ?
      selectedContent
    : isSingletonContentSection(section) ?
      getSectionSingletonContentItem(state, section)
    : isOrderedContentSection(section) ?
      head(getSectionOrderedContentItems(state, section))
    :
      null;
};

export const getSectionSingletonContentItem = (state, sectionOrId)=>
  fromPageEntities.getSectionSingletonContentItem(state.pageEntities, sectionOrId);

export const getSelectedSubmenuItem = (state, subpageOrId, content, sectionOrId)=>
  fromPageEntities.getSelectedSubmenuItem(state.pageEntities, subpageOrId, content, sectionOrId);

export const getSerializableSubpageData = (state, subpageOrId)=> {
  const subpage = getSubpageById(state, subpageOrId);
  const displayName = getSubpageDisplayName(state, subpage);

  // @TODO in a future version we may want to handle including submenu items in
  // the creation from this subpageData. The challenging piece is transposing
  // submenu data such as sectionId and contentUuid to the new identification
  // strings which are assigned when the content is recreated in the new
  // subpage.

  return {
    originalSubpageData: {
      ...omit(subpage, ['orderedSectionIds']),
      displayName
    },
    serializableSections: map(subpage.orderedSectionIds, (sectionId) => {
      const serializableSectionsAndContentData =
        getSerializableSectionAndContentData(state, sectionId);

      return serializableSectionsAndContentData;
    })
  };
};

export const getSerializableSectionAndContentData = (state, sectionOrId)=> {
  const sectionId = getIdFrom(sectionOrId);
  const section = getSectionById(state, sectionId);

  var contentItems = [];
  var nestedSectionsData = [];

  if (isSingletonContentSection(section)) {
    contentItems = [getSectionSingletonContentItem(state, section)];

  } 
  else if (isOrderedContentSection(section)) {
    contentItems = getSectionOrderedContentItems(state, section);

  } else if (isGridPositionsContentSection(section)) {
    contentItems = map(getSectionGridPositions(state, section), (gridPos)=> {
      return {
        ...gridPos,
        ...getContentItemByUuid(state, gridPos.uuid)
      };
    });

  } else {
    throw new Error(`Unexpected section type selected for serialization: "${section.type}"`);

  }

  const backgroundData = getSectionBackgroundData(state, section);
  const fallbackBackgroundData = getSectionFallbackBackgroundData(state, section);

  const {
    asset: backgroundAsset 
  } = getSectionBackgroundContentAndAssetFromBackgroundData(state, backgroundData);

  const {
    asset: fallbackBackgroundAsset
  } = getSectionBackgroundContentAndAssetFromBackgroundData(state, fallbackBackgroundData);

  if (section.type === SECTION_TYPE_TABS) { // for tabs, we need to serialize the nested sections data
    nestedSectionsData = compact(map(section.tabs, (tab) => tab.content.sectionId ? getSerializableSectionAndContentData(state, tab.content.sectionId) : null));
  }

  return {
    originalSectionData: {
      ...omit(section, ['id', 'singletonContentUuid', 'orderedContentUuids', 'gridPositions']),
      originalSectionId: sectionId
    },
    contentItems: compact(contentItems),
    backgroundAsset: backgroundAsset,
    fallbackBackgroundAsset: fallbackBackgroundAsset,
    nestedSectionsData: nestedSectionsData
  };
};

export const getSerializableTileData = (state, contentItemOrUuid)=> {
  const contentUuid = getUuidFrom(contentItemOrUuid);
  const contentItem = getContentItemByUuid(state, contentUuid);
  const section = getSectionForContent(state, contentItem);
  const { sizex, sizey } = find(section.gridPositions, ['uuid', contentUuid]);
  const contentItemData = getSerializableContentDataForCopy(contentItem);

  return { contentItemData, sizex, sizey };
};

export const getShouldShowFooter = (state)=> {
  const currentSubpage = getCurrentSubpage(state);
  const sections = map(currentSubpage.orderedSectionIds, sectionId => 
    getSectionById(state, sectionId)
  );
  const isFullPage = getIsCurrentSubpageFullPageSection(state);

  // multisection subpages and fullpage text sections should always show a footer
  return isFullPage ? head(sections).type === 'text' : true;
};

export const getShouldShowHeader = (state)=>
  fromPageEntities.getShouldShowHeader(state.pageEntities);

export const getShouldShowSocialLinks = (state)=>
  fromPageEntities.getShouldShowSocialLinks(state.pageEntities);

export const getShouldShowTitle = (state)=>
  fromPageEntities.getShouldShowTitle(state.pageEntities);

export const getSocialLinks = (state)=>
  fromPageEntities.getSocialLinks(state.pageEntities);

export const getIsInlineHeaderStyle = (state) => 
  fromPageEntities.getIsInlineHeaderStyle(state.pageEntities);

export const getDisplayAnchorLinks = (state) => 
  fromPageEntities.getDisplayAnchorLinks(state.pageEntities);

export const getIsFullBleed = (state) => 
  fromPageEntities.getIsFullBleed(state.pageEntities);

export const getHeaderContentPosition = (state) => 
  fromPageEntities.getHeaderContentPosition(state.pageEntities);

export const getIsUsingLegacyHeader = (state) => 
  fromPageEntities.getIsUsingLegacyHeader(state.pageEntities);

export const getSubpageDisplayName = (state, subpageOrId)=>
  fromPageEntities.getSubpageDisplayName(state.pageEntities, subpageOrId);

export const getSubpages = (state) =>
  fromPageEntities.getSubpages(state.pageEntities);

export const getSubpageById = (state, id)=>
  fromPageEntities.getSubpageById(state.pageEntities, id);

export const getSubpageBySlugOrId = (state, slugOrId)=>
  fromPageEntities.getSubpageBySlugOrId(state.pageEntities, slugOrId);

export const getSubpageForContent = (state, contentOrUuid)=>
  fromPageEntities.getSubpageForContent(state.pageEntities, contentOrUuid);

export const getSubpageForSection = (state, sectionOrId)=>
  fromPageEntities.getSubpageForSection(state.pageEntities, sectionOrId);

export const getSubpageIdToOrderedSubmenuItemIdsMap = (state)=>
  fromPageEntities.getSubpageIdToOrderedSubmenuItemIdsMap(state.pageEntities);

export const getSubpagesWithDisplayNames = (state)=>
  fromPageEntities.getSubpagesWithDisplayNames(state.pageEntities);

export const getTileMobileOrderPriorityByUuid  = (state, section)=>
  fromPageEntities.getTileMobileOrderPriorityByUuid(state.pageEntities, section);

export const getSectionGridTaxonomy = (state, sectionOrId)=>
  fromPageEntities.getSectionGridTaxonomy(state.pageEntities, sectionOrId);

export const getSectionGridTaxonomyCategory = (state, sectionOrId, categoryId)=>
  fromPageEntities.getSectionGridTaxonomyCategory(state.pageEntities, sectionOrId, categoryId);

export const getMainSectionForSubpage = (state, subpage)=>
  fromPageEntities.getMainSectionForSubpage(state.pageEntities, subpage);

export const getUseFixedNavLinks = (state)=>
  fromTheme.getUseFixedNavLinks(state.theme);

// these are never updated via Redux, they only handle the initial state load
export const getConsentProps = (state)=>
  fromConsent.getConsentProps(state.consent);

// translations
export const getTranslationsByIso = (state, iso)=>
  fromTranslations.getTranslationsByIso(state.translations, iso);

export const getTranslationsStatusByIso = (state)=>
  fromTranslations.getTranslationsStatusByIso(state.translations);
